@import "tailwindcss";

/* Brand theme — overrides Tailwind defaults with BeyondFrames Studio tokens.
 * `--color-black` is intentionally re-pointed at the brand ink so utilities
 * like `text-black` / `bg-black` already match the design system. */
@theme {
  /* Brand gold ramp — mirrors the reference site's --gold tokens
   * (beyondframesstudio.com / template-woo). Names kept as `teal-*` for backwards
   * compatibility with existing utilities; the values are the warm gold. */
  --color-teal:        #a8763e;   /* --gold    */
  --color-teal-dark:   #8f6133;   /* --gold-d  */
  --color-teal-light:  #c49a5a;   /* --gold-l  */
  --color-teal-xl:     #f0e4cc;   /* --gold-xl */
  --color-teal-dim:    rgba(168, 118, 62, 0.12);
  --color-teal-border: rgba(168, 118, 62, 0.35);
  --color-soft-border: rgba(168, 118, 62, 0.25);  /* --border on reference cards */
  --color-black:       #0a0a0f;
  --color-white:       #ffffff;
  --color-mute:        #8888a0;
  --color-mute-light:  #b0b0c8;
  --color-cream:       #f5f0e8;   /* --cream  */
  --color-cream2:      #ede5d5;   /* --cream2 */
  --color-cream3:      #e5dac8;   /* --cream3 */
  --color-cream-deep:  #f1e9df;   /* reference body background */
  --color-warm-brown:  #6b5d50;   /* --muted, body text */

  /* Soft warm card shadow — reference --shadow. */
  --shadow-soft: 0 4px 24px rgba(100, 70, 30, 0.13);

  /* Reference (template-woo) uses Inter for body AND headings — no serif.
   * `font-serif` utilities now resolve to Inter so the widget headings match
   * the woo product/preview pages. */
  --font-serif: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  --font-sans:  'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;

  /* ── rem re-base for the Dawn host ─────────────────────────────────────
   * Dawn sets `html { font-size: calc(var(--font-body-scale) * 62.5%) }`,
   * i.e. 1rem = 10px (body_scale 100). Tailwind's rem-based scale assumes a
   * 16px root, so the whole widget would render at 62.5%. Multiplying every
   * rem-derived base token by 1.6 (16 ÷ 10) restores the intended sizing.
   * All spacing utilities derive from `--spacing`, so one override fixes
   * every p-/m-/gap-/w-/h-/size-*; text + container + radius scales follow.
   * Scoped to the widget bundle — Dawn never reads these vars. The preview
   * harness simulates the same 62.5% root so both environments match. */
  --spacing:       0.4rem;   /* 0.25 × 1.6 */
  --text-xs:       1.2rem;   /* 0.75  × 1.6 */
  --text-sm:       1.4rem;   /* 0.875 × 1.6 */
  --text-base:     1.6rem;   /* 1.0   × 1.6 */
  --text-lg:       1.8rem;   /* 1.125 × 1.6 */
  --text-xl:       2rem;     /* 1.25  × 1.6 */
  --text-2xl:      2.4rem;   /* 1.5   × 1.6 */
  --container-md:  44.8rem;  /* 28rem × 1.6 */
  --container-2xl: 67.2rem;  /* 42rem × 1.6 */
  --radius-md:     0.6rem;   /* 0.375rem × 1.6 */
  --radius-lg:     0.8rem;   /* 0.5rem   × 1.6 */
  --radius-xl:     1.2rem;   /* 0.75rem  × 1.6 */
  --radius-2xl:    1.6rem;   /* 1rem     × 1.6 */
}

/* Scope: the widget bundle is loaded site-wide via theme.liquid, so guard
 * styles against affecting Dawn's pages. All widget DOM is rendered inside
 * elements with class `bf-widget` (set in main.tsx). */
.bf-widget {
  font-family: var(--font-sans);
  color: var(--color-black);
}

/* Every clickable control in the widget shows a pointer (Dawn's reset removes
 * it from <button>). Covers buttons, role="button" dropzones, selects, links. */
.bf-widget button:not(:disabled),
.bf-widget [role="button"],
.bf-widget [role="radio"],
.bf-widget select,
.bf-widget a,
.bf-widget label[for] {
  cursor: pointer;
}
.bf-widget button:disabled { cursor: not-allowed; }

/* Dawn's UNLAYERED `h1..h5 { font-weight: var(--font-heading-weight) }` (400)
 * beats Tailwind's LAYERED font-* utilities (cascade layers lose to unlayered
 * rules regardless of specificity), so `font-bold` etc. on widget headings get
 * ignored. Revert these props to the utility-layer value so the heading's own
 * font-weight / family / spacing utilities apply again. */
.bf-widget :is(h1, h2, h3, h4, h5, h6) {
  font-weight: revert-layer;
  font-family: revert-layer;
  letter-spacing: revert-layer;
  line-height: revert-layer;
}

/* ─── Reference motion — selected style-card glow + "live" dot pulse ────────
 * Ported from template-woo (@keyframes glow / dot-pulse). The glow gives the
 * chosen style card the pulsing gold ring the reference upload flow uses to
 * signal the active selection. */
@keyframes bf-glow {
  from {
    box-shadow:
      0 0 0 4px rgba(255, 179, 71, 0.20),
      0 10px 25px rgba(255, 179, 71, 0.25);
  }
  to {
    box-shadow:
      0 0 0 6px rgba(255, 179, 71, 0.35),
      0 14px 35px rgba(255, 179, 71, 0.45);
  }
}

@keyframes bf-dot-pulse {
  0%   { box-shadow: 0 0 0 0   rgba(62, 207, 142, 0.6); background: #3ecf8e; }
  50%  { box-shadow: 0 0 0 6px rgba(62, 207, 142, 0.0); background: #22c55e; }
  100% { box-shadow: 0 0 0 0   rgba(62, 207, 142, 0.6); background: #3ecf8e; }
}

/* Helper utilities used by the React components (keyframes can't be expressed
 * as Tailwind arbitrary values). */
.bf-glow      { animation: bf-glow 1.8s infinite alternate; }
.bf-live-dot  { animation: bf-dot-pulse 1.6s ease-in-out infinite; }

/* Sweeping highlight that runs across the progress-bar fill while generating. */
@keyframes bf-bar-shimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
.bf-bar-shimmer { animation: bf-bar-shimmer 1.4s ease-in-out infinite; }

/* "Still working…" dots shown once real processing runs past the 80s mock
 * progress window, so the bar's message visibly keeps moving instead of
 * looking frozen while the job finishes for real. */
@keyframes bf-bar-dots {
  0%, 20%  { opacity: 0; }
  50%      { opacity: 1; }
  100%     { opacity: 0; }
}
.bf-bar-overtime { animation: bf-bar-fade-in 0.4s ease-out; }
@keyframes bf-bar-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
.bf-bar-dots span {
  display: inline-block;
  animation: bf-bar-dots 1.4s infinite;
}
.bf-bar-dots span:nth-child(2) { animation-delay: 0.2s; }
.bf-bar-dots span:nth-child(3) { animation-delay: 0.4s; }

/* TEMP: hide the "Large Canvas" format option. Remove this rule to show it again. */
.bf-fmt-card[data-packet="Large Canvas"] { display: none !important; }

/* Block iOS long-press "Save Image" / drag on the generated portrait. */
.bf-portrait-img {
  -webkit-touch-callout: none !important;
  -webkit-user-select: none !important;
  user-select: none !important;
  -webkit-user-drag: none !important;
}

/* ─── Moments rows — image + text side by side (woo bf-moment-odd/even).
 * Raw CSS (not Tailwind `sm:` utilities, whose range media query can fail to
 * apply here and collapse the row to one stacked column). */
.bf-moment-row {
  display: flex;
  align-items: center;
  gap: 28px;
}
.bf-moment-row--rev { flex-direction: row-reverse; }
.bf-moment-media { flex: 0 0 200px; max-width: 200px; }
.bf-moment-media img {
  width: 100%;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  border-radius: 14px;
  display: block;
}
.bf-moment-text { flex: 1 1 auto; min-width: 0; }
@media (max-width: 640px) {
  .bf-moment-row,
  .bf-moment-row--rev { flex-direction: column; }
  .bf-moment-media { flex: none; width: 100%; max-width: 100%; }
  /* "Your name (optional)" / "Pet name (optional)" wrap to two lines in the
   * narrow 50/50 columns — shrink to 10px + tighter tracking to keep 1 line. */
  .bf-name-label {
    font-size: 10px !important;
    letter-spacing: 0.06em !important;
  }
}

/* ─── Canvas upsell review card ────────────────────────────────────────────
 * Same behaviour as .bf-moment-row: image + text side by side on desktop, and
 * STACKED (image on top, text below) on mobile. Raw CSS media query (Tailwind
 * `sm:` can fail to apply here). Image keeps a 3:4 portrait ratio like
 * .bf-moment-media. */
.bf-cv-review {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 18px;
}
.bf-cv-review-media {
  flex: 0 0 180px;
  width: 180px;
  aspect-ratio: 3 / 4;
  border-radius: 14px;
  overflow: hidden;
  background: var(--color-cream2);
}
.bf-cv-review-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.bf-cv-review-text { flex: 1 1 auto; min-width: 0; }
@media (max-width: 640px) {
  .bf-cv-review { flex-direction: column; gap: 14px; padding: 14px; }
  .bf-cv-review-media { flex: none; width: 100%; }
}

/* ─── Preview page two-column layout ───────────────────────────────────────
 * Written as raw CSS with a standard `max-width` media query (NOT Tailwind's
 * `md:` range-syntax `@media (width>=48rem)`, which can fail to apply in some
 * environments and collapse the layout to one column). Image 58% / format 42%
 * side by side ≥768px; stacked below. */
.bf-preview-layout {
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-items: flex-start;
}
.bf-preview-image-col {
  flex: 1 1 auto;
  min-width: 0;
}
.bf-preview-format-col {
  /* Fixed-width right column (matches woo bf-preview-wrap: 1fr 420px). */
  flex: 0 0 420px;
  max-width: 420px;
  min-width: 0;
  position: sticky;
  top: 20px;
}
@media (max-width: 768px) {
  .bf-preview-layout { flex-direction: column; }
  .bf-preview-image-col,
  .bf-preview-format-col {
    flex: none;
    width: 100%;
    position: static;
  }
}
